"use client";

// WO-GF-001-P1B-S4 — S3 POPUP FRAMEWORK (item1: W2 + W8; item3: N2 FAQ accordion). Document-scoped hydrator, same family as
// HubChromeLayer / LandingFunnelLayer: it wires the CAPTURED (dead-photocopy) home/landing/route sections
// WITHOUT mutating their DOM at rest, so the pixel gate stays green (listeners + data-behavior hooks + a
// display:none portal only). §0f capture (live snowbus.com, read-only) settled what each does:
//   • W2 "Learn more" (8× a.orangeLink inside .cols cards) — LIVE renders a href-LESS anchor whose React
//     onClick SPA-navigates to /general-information#<slug>; slug = card heading lowercased, spaces→"-",
//     "&"/"," dropped (verified live: "BUY, BOOK, RENT" → /general-information#buy-book-rent, dialogCount 0).
//     It is NOT a popup — the founder's W2 "cards don't open their popups" is imprecise; live is dead-JS
//     NAVIGATION. Faithful port (F35) = wire the same nav. (Behaviourally S5-class; built here per the WO's
//     ITEM-1 scoping — flagged in the close.)
//   • W8 "Become a Sponsor" (button[type=submit]) — LIVE opens the Sponsors modal. Content source-faithful to
//     snowbus-app/src/modals/Sponsors/component/Sponsors.js ("Associative / Advertising & Marketing", the
//     SNOWGROUP body, "Join our growing list of partners", "Contact us"). The legacy modal SHELL CSS
//     (mountainPopup infoModal sponsorModal) and its two decorative SVGs are legacy @assets not in this build,
//     so the shell is the shared ModalShell (PH2-RESKIN, same posture as the transport/funnel modals) and the
//     decorative logos are omitted (legacy-asset gap, flagged). The legacy nested ContactUs modal → the
//     ported /contact-us content page (the modal framework routed there).
//
// Mounted by page.tsx wherever the served body carries the markers (desktop only; F30 mobile stays mirror).
// Harness hooks: data-behavior="learn-more" / "sponsor-open"; data-modal="sponsor".

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ModalShell } from "./ModalShell";

// legacy slug (snowbus-app GeneralInformation.js):
//   e.h.toLowerCase().split(" ").filter(e => e && e !== "&").join("-").split(",").join("")
function giSlug(heading: string): string {
  return heading
    .toLowerCase()
    .split(" ")
    .filter((w) => w && w !== "&")
    .join("-")
    .split(",")
    .join("");
}

export default function PopupLayer() {
  const [sponsorOpen, setSponsorOpen] = useState(false);

  useEffect(() => {
    const cleanups: Array<() => void> = [];

    // ---- W2 · "Learn more" cards — href-less a.orangeLink → navigate to /general-information#<card slug>. ----
    for (const a of Array.from(
      document.querySelectorAll<HTMLAnchorElement>("a.orangeLink"),
    )) {
      if (!/^\s*learn more\s*$/i.test(a.textContent || "")) continue;
      const card = a.closest<HTMLElement>(".cols");
      const heading = (card?.querySelector("h3, h4")?.textContent || "").trim();
      if (!heading) continue;
      const href = `/general-information#${giSlug(heading)}`;
      a.setAttribute("data-behavior", "learn-more");
      a.style.cursor = "pointer";
      const go = (e: Event) => {
        e.preventDefault();
        window.location.assign(href);
      };
      a.addEventListener("click", go);
      cleanups.push(() => a.removeEventListener("click", go));
    }

    // ---- W8 · "Become a Sponsor" (button[type=submit]) → open the Sponsors modal. ----
    for (const b of Array.from(
      document.querySelectorAll<HTMLElement>("button, [role='button'], a"),
    )) {
      if (!/become a sponsor/i.test(b.textContent || "")) continue;
      b.setAttribute("data-behavior", "sponsor-open");
      b.style.cursor = "pointer";
      const open = (e: Event) => {
        e.preventDefault(); // type=submit — never let it submit the captured hero form
        setSponsorOpen(true);
      };
      b.addEventListener("click", open);
      cleanups.push(() => b.removeEventListener("click", open));
    }

    // ---- N2 · FAQ accordion (/faq) — the Bootstrap accordion headers are dead in the mirror (JS stripped);
    // every Q&A body is captured (5 open + ~29 collapsed at rest). Wire each header to toggle its own body
    // (independent / always-open — the captured multi-open rest state proves it is not single-open). Rest
    // state is left exactly as captured, so the pixel gate holds; toggling happens only on click. ----
    for (const header of Array.from(
      document.querySelectorAll<HTMLElement>(".faqAccordian .accordion-button"),
    )) {
      const panel = header
        .closest<HTMLElement>(".accordion-item")
        ?.querySelector<HTMLElement>(".accordion-collapse");
      if (!panel) continue;
      header.setAttribute("data-behavior", "faq-accordion");
      const toggle = (e: Event) => {
        e.preventDefault();
        const open = panel.classList.contains("show");
        panel.classList.toggle("show", !open);
        header.setAttribute("aria-expanded", String(!open));
      };
      header.addEventListener("click", toggle);
      cleanups.push(() => header.removeEventListener("click", toggle));
    }

    return () => cleanups.forEach((c) => c());
  }, []);

  return (
    <>
      <span data-popup-layer hidden />
      {sponsorOpen &&
        createPortal(
          <SponsorModal onClose={() => setSponsorOpen(false)} />,
          document.body,
        )}
    </>
  );
}

// SNOWGROUP sponsor modal — source-faithful content (snowbus-app/src/modals/Sponsors). Legacy class names kept
// as hooks/reference; layout is inline (PH2-RESKIN shell, legacy sponsorModal CSS not ported).
function SponsorModal({ onClose }: { onClose: () => void }) {
  return (
    <ModalShell title="" onClose={onClose} data-modal="sponsor">
      <div
        className="infoContent sponsorModal"
        style={{ textAlign: "center", padding: "0 8px 8px", fontFamily: '"Roboto", system-ui, sans-serif' }}
      >
        <h3 style={{ fontSize: 22, fontWeight: 700, margin: "4px 0 16px", lineHeight: 1.25, color: "#2b2b2b" }}>
          <span style={{ display: "block", color: "#71abb8" }}>Associative</span>
          Advertising &amp; Marketing
        </h3>
        <div className="text" style={{ fontSize: 14, lineHeight: 1.6, color: "#555", maxWidth: 560, margin: "0 auto" }}>
          The <strong>SNOWGROUP</strong> puts a unique &amp; fresh &ldquo;snow-culture&rdquo; spin on winter
          sports travel. businesses involved in our sponsorship, merchant&nbsp; programs, &amp; snowclub, can
          create exciting campaigns &amp; specialized product offerings both online and offline. A unique
          opportunity to Market brands and services across multiple mountains.
        </div>
        <span className="btmText" style={{ display: "block", margin: "18px 0", fontWeight: 600, color: "#2b2b2b" }}>
          Join our growing list of partners
        </span>
        <a
          className="cntctBtn"
          href="/contact-us"
          style={{
            display: "inline-block",
            padding: "10px 30px",
            borderRadius: 5,
            background: "#f15a22",
            color: "#fff",
            textDecoration: "none",
            fontWeight: 500,
          }}
        >
          Contact us
        </a>
      </div>
    </ModalShell>
  );
}
